import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Trash2, MessageCircle, ChevronRight, X, User, Bot } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminConversations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [tLoading, setTLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/conversations`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openTranscript = async (sessionId) => {
    setOpenId(sessionId);
    setTranscript(null);
    setTLoading(true);
    try {
      const r = await axios.get(`${API}/admin/conversations/${sessionId}`, { withCredentials: true });
      setTranscript(r.data);
    } catch (e) {
      toast.error("Failed to load transcript");
    } finally {
      setTLoading(false);
    }
  };

  const remove = async (sessionId) => {
    if (!window.confirm("Delete this entire conversation?")) return;
    try {
      await axios.delete(`${API}/admin/conversations/${sessionId}`, { withCredentials: true });
      setItems((prev) => prev.filter((c) => c.session_id !== sessionId));
      if (openId === sessionId) { setOpenId(null); setTranscript(null); }
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-conversations">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Chat transcripts</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Conversations</h1>
          <p className="mt-3 text-[#A9A9A6] text-sm max-w-lg">
            Every question a visitor asks the JJ AI Assistant lands here. Great signal for what recruiters and prospects actually want to know.
          </p>
        </div>
        <span className="text-xs text-[#A9A9A6]">{items.length} total</span>
      </div>

      {(() => {
        if (loading) {
          return <div className="mt-10 text-[#A9A9A6]">Loading…</div>;
        }
        if (items.length === 0) {
          return (
            <div className="mt-10 border border-[#343437] p-10 text-center text-[#A9A9A6]" data-testid="conversations-empty">
              <MessageCircle size={20} className="mx-auto text-[#D8A936] mb-3" />
              No conversations yet. Once visitors start using the JJ AI Assistant, transcripts will appear here.
            </div>
          );
        }
        return (
          <div className="mt-8 border border-[#343437]">
            <div className="hidden md:grid grid-cols-[1fr_180px_120px_60px] gap-0 border-b border-[#343437] bg-[#151517]">
              {["First message", "Last activity", "Messages", ""].map((h) => (
                <div key={h} className="px-4 py-3 text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">{h}</div>
              ))}
            </div>
            {items.map((c) => (
              <div
                key={c.session_id}
                className="border-t border-[#343437] first:border-t-0 grid grid-cols-1 md:grid-cols-[1fr_180px_120px_60px] gap-0 hover:bg-[#151517] transition-colors"
                data-testid={`conversation-row-${c.session_id}`}
              >
                <button
                  onClick={() => openTranscript(c.session_id)}
                  className="px-4 py-4 text-left text-sm text-[#FAF9F6] hover:text-[#D8A936] transition-colors flex items-center gap-2"
                >
                  <ChevronRight size={14} className="text-[#D8A936] shrink-0" />
                  <span className="line-clamp-2">{c.preview || "(no user message yet)"}</span>
                </button>
                <div className="px-4 py-4 text-xs text-[#A9A9A6]">{formatWhen(c.last_at)}</div>
                <div className="px-4 py-4 text-xs text-[#FAF9F6]">{c.message_count}</div>
                <div className="px-4 py-4 flex items-start justify-end">
                  <button
                    onClick={() => remove(c.session_id)}
                    data-testid={`conversation-delete-${c.session_id}`}
                    aria-label="Delete conversation"
                    className="p-2 text-[#A9A9A6] hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Transcript drawer */}
      {openId && (
        <div
          className="fixed inset-0 z-[80] bg-[#0B0B0C]/85 backdrop-blur-sm flex items-stretch justify-end"
          onClick={() => { setOpenId(null); setTranscript(null); }}
          data-testid="transcript-drawer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0B0B0C] border-l border-[#343437] w-full max-w-xl h-full flex flex-col"
          >
            <div className="border-b border-[#343437] px-6 py-4 flex items-center justify-between">
              <div>
                <p className="eyebrow">Transcript</p>
                <p className="text-xs text-[#A9A9A6] mt-1 font-mono">{openId}</p>
              </div>
              <button
                onClick={() => { setOpenId(null); setTranscript(null); }}
                aria-label="Close"
                className="p-2 text-[#A9A9A6] hover:text-[#D8A936] transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4" data-testid="transcript-body">
              {tLoading && <p className="text-[#A9A9A6] text-sm">Loading…</p>}
              {transcript?.messages?.map((m, i) => (
                <div key={`${m.created_at || "msg"}-${i}`} data-testid={`transcript-msg-${i}`}>
                  <div className="flex items-center gap-2 mb-2 text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">
                    {m.role === "user" ? <User size={12} className="text-[#D8A936]" /> : <Bot size={12} className="text-[#D8A936]" />}
                    <span>{m.role === "user" ? "Visitor" : "JJ AI"}</span>
                    <span className="ml-auto">{formatWhen(m.created_at)}</span>
                  </div>
                  <div className={`text-[14px] leading-relaxed whitespace-pre-wrap px-4 py-3 ${
                    m.role === "user"
                      ? "bg-[#D8A936]/10 border-l-2 border-[#D8A936] text-[#FAF9F6]"
                      : "bg-[#151517] border border-[#343437] text-[#FAF9F6]"
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
